import tokenService from '../../../services/auth/tokenService';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = tokenService.getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export const deviceService = {
  async fetchOrganizationData(organizationId) {
    try {
      // Fetch organization data
      const orgResponse = await fetch(`${API_BASE_URL}/organizations/${organizationId}`, {
        headers: getAuthHeaders()
      });
      if (!orgResponse.ok) throw new Error('Failed to fetch organization data');
      const organization = await orgResponse.json();

      // Fetch networks data
      const networksResponse = await fetch(`${API_BASE_URL}/networks?organization_id=${organizationId}`, {
        headers: getAuthHeaders()
      });
      if (!networksResponse.ok) throw new Error('Failed to fetch networks data');
      const networks = await networksResponse.json();

      // Fetch devices data (organization filtering is now handled by backend)
      const devicesResponse = await fetch(`${API_BASE_URL}/envis-devices`, {
        headers: getAuthHeaders()
      });
      let devices = [];
      if (devicesResponse.ok) {
        const devicesData = await devicesResponse.json();
        devices = devicesData.devices || [];
      }

      // Fetch sections for each network
      const networksWithSections = await Promise.all(
        networks.map(async (network) => {
          try {
            const sectionsResponse = await fetch(`${API_BASE_URL}/networks/${network.id}/sections`, {
              headers: getAuthHeaders()
            });
            let sections = [];
            if (sectionsResponse.ok) {
              sections = await sectionsResponse.json();
            }
            
            const networkDevices = devices.filter(device => device.network_id === network.id);
            return {
              ...network,
              sections: sections,
              devices: networkDevices,
              deviceCount: networkDevices.length
            };
          } catch (error) {
            console.error(`Failed to fetch sections for network ${network.id}:`, error);
            // Return network without sections if fetch fails
            const networkDevices = devices.filter(device => device.network_id === network.id);
            return {
              ...network,
              sections: [],
              devices: networkDevices,
              deviceCount: networkDevices.length
            };
          }
        })
      );

      return {
        organization,
        networks: networksWithSections,
        totalNetworks: networksWithSections.length,
        totalDevices: devices.length
      };
    } catch (error) {
      throw new Error(`Failed to fetch organization data: ${error.message}`);
    }
  },

  async fetchDevicesForOrganization(organizationId, limit, offset) {
    try {
      // Note: organizationId parameter is kept for compatibility but filtering is now handled by backend
      const response = await fetch(
        `${API_BASE_URL}/envis-devices?limit=${limit}&offset=${offset}`,
        {
          headers: getAuthHeaders()
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch devices for organization');
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Failed to fetch devices: ${error.message}`);
    }
  },

  async registerDevice(deviceData) {
    try {
      const response = await fetch(`${API_BASE_URL}/envis-devices`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(deviceData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to register device');
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Failed to register device: ${error.message}`);
    }
  },

  async updateDevice(deviceId, deviceData) {
    try {
      const response = await fetch(`${API_BASE_URL}/envis-devices/${deviceId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(deviceData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update device');
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Failed to update device: ${error.message}`);
    }
  },

  async getDeletePreview(deviceId) {
    try {
      const response = await fetch(`${API_BASE_URL}/envis-devices/${deviceId}/delete-preview`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch delete preview');
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Failed to fetch delete preview: ${error.message}`);
    }
  },

  async deleteDevice(deviceId) {
    try {
      const response = await fetch(`${API_BASE_URL}/envis-devices/${deviceId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete device');
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Failed to delete device: ${error.message}`);
    }
  }
}; 